import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import commandxLogo from "@/assets/commandx-logo.png";
import { useAuth } from "@/contexts/AuthContext";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import { ParticleBackground } from "@/components/ui/particle-background";
import { Loader2, HardHat, BarChart3, CheckCircle2, Users, Shield, Mail } from "lucide-react";
import { toast } from "sonner";

import { GoogleIcon } from "@/components/icons/GoogleIcon";
import { AppleIcon } from "@/components/icons/AppleIcon";
import { NetworkErrorBanner } from "@/components/auth/NetworkErrorBanner";
import { isNetworkError } from "@/utils/authNetwork";
import { useIsPlatformAdmin } from "@/hooks/usePlatformSummary";
import { supabase } from "@/integrations/supabase/client";

type AuthMode = "signin" | "signup" | "forgot";

const Auth = () => {
  const navigate = useNavigate();
  
  const { user, signIn, signUp, signInWithGoogle, signInWithApple, loading: authLoading } = useAuth();
  const { data: isPlatformAdmin, isLoading: isAdminLoading } = useIsPlatformAdmin();
  const [isLoading, setIsLoading] = useState(false);
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  const [showNetworkError, setShowNetworkError] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [mode, setMode] = useState<AuthMode>("signin");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    if (user && !authLoading && !isAdminLoading) {
      navigate(isPlatformAdmin ? "/platform" : "/controller");
    }
  }, [user, authLoading, isAdminLoading, isPlatformAdmin, navigate]);

  const handleLogin = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setIsLoading(true);
    setShowNetworkError(false);
    try {
      const { error } = await signIn(email, password);
      if (error) {
        if (isNetworkError(error) || error.message.includes("Can't reach") || error.message.includes("check your connection")) {
          setShowNetworkError(true);
        }
        toast.error(error.message);
      } else {
        toast.success("Logged in successfully");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setIsLoading(true);
    setShowNetworkError(false);
    try {
      const { error } = await signUp(email, password);
      if (error) {
        if (isNetworkError(error) || error.message.includes("Can't reach")) {
          setShowNetworkError(true);
        }
        toast.error(error.message);
      } else {
        toast.success("Check your email to verify your account");
        setMode("signin");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (error) {
        toast.error(error.message);
      } else {
        toast.success("Password reset link sent — check your email");
        setMode("signin");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsOAuthLoading(true);
    setShowNetworkError(false);
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        if (isNetworkError(error) || error.message.includes("Can't reach")) {
          setShowNetworkError(true);
        }
        toast.error(error.message);
        setIsOAuthLoading(false);
      }
    } catch {
      setIsOAuthLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    setIsOAuthLoading(true);
    setShowNetworkError(false);
    try {
      const { error } = await signInWithApple();
      if (error) {
        if (isNetworkError(error) || error.message.includes("Can't reach")) {
          setShowNetworkError(true);
        }
        toast.error(error.message);
        setIsOAuthLoading(false);
      }
    } catch {
      setIsOAuthLoading(false);
    }
  };

  const handleRetry = () => {
    setShowNetworkError(false);
    if (email && password) {
      handleLogin();
    }
  };

  // Safari safety timeout: if auth hangs for 5s, force-resolve loading
  const [forceReady, setForceReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setForceReady(true), 5000);
    return () => clearTimeout(t);
  }, []);

  if (authLoading && !forceReady) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const features = [
    { icon: HardHat, label: "Real-time project tracking" },
    { icon: CheckCircle2, label: "Streamlined approval workflows" },
    { icon: BarChart3, label: "Financial oversight & reporting" },
    { icon: Shield, label: "Full audit trail & compliance" },
  ];

  const headingText = mode === "signup" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Welcome back";
  const subText = mode === "signup"
    ? "Sign up to get started with your workspace"
    : mode === "forgot"
    ? "Enter your email and we'll send a reset link"
    : "Sign in to your workspace to continue";

  return (
    <div className="relative flex min-h-screen items-start justify-center bg-background px-4 pt-[8vh] overflow-hidden">
      <SEO
        title={mode === "signup" ? "Sign Up" : mode === "forgot" ? "Reset Password" : "Sign In"}
        description="Sign in to access Command X business management platform"
        keywords="sign in, login, authentication"
        noIndex={true}
      />
      <div className="aurora-bg absolute inset-0 z-0" />
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-aurora-float-1" style={{ willChange: 'transform' }} />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/15 rounded-full blur-3xl animate-aurora-float-2" style={{ willChange: 'transform' }} />
        <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-primary/10 rounded-full blur-3xl animate-aurora-float-3" style={{ willChange: 'transform' }} />
      </div>
      <ParticleBackground className="z-[1]" particleCount={60} />

      <div className="relative z-10 w-full max-w-4xl flex flex-col items-center">
        <div className="flex flex-col items-center mb-1">
          <img src={commandxLogo} alt="CommandX" className="h-[400px] w-auto max-w-[900px] object-contain -my-16" />
          <p className="text-lg font-medium text-foreground/80 -mt-2">
            All your projects. One <span className="text-primary font-bold">command</span>.
          </p>
        </div>
      <Card className="w-full glass overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* LEFT HERO PANEL */}
          <div className="hidden md:flex flex-col justify-center p-8 lg:p-10 bg-gradient-to-br from-primary/10 via-transparent to-primary/5 border-r border-border/50">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold font-heading leading-tight mb-3">
                <span className="text-gradient">Every project.</span><br />Every deadline.<br />Under control.
              </h1>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                The all-in-one platform for construction teams to manage projects, approvals, and financials in one place.
              </p>
              <ul className="space-y-3">
                {features.map(({ icon: Icon, label }) => (
                  <li key={label} className="flex items-center gap-3 text-sm">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 ring-1 ring-primary/20">
                      <Icon className="h-3.5 w-3.5 text-primary" />
                    </span>
                    <span className="text-foreground/80">{label}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="pt-6 mt-6 border-t border-border/50">
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <Users className="h-3.5 w-3.5 text-primary" />
                Built for construction teams of every size
              </p>
            </div>
          </div>

          {/* RIGHT FORM PANEL */}
          <div className="p-6 lg:p-8 flex flex-col justify-center">
            <div className="mb-4">
              <h2 className="text-xl font-bold font-heading">{headingText}</h2>
              <p className="text-sm text-muted-foreground mt-1">{subText}</p>
            </div>
            <div className="space-y-4">
              {mode === "forgot" ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">Email</Label>
                    <Input id="reset-email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-secondary border-border" />
                  </div>
                  <Button type="submit" className="w-full" variant="glow" disabled={isLoading}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Send Reset Link
                  </Button>
                  <button type="button" className="w-full text-sm text-primary hover:underline" onClick={() => setMode("signin")}>
                    ← Back to Sign In
                  </button>
                </form>
              ) : (
                <>
                  <div className="space-y-3">
                    <Button type="button" variant="outline" className="w-full bg-secondary/50 border-border hover:bg-secondary" onClick={handleGoogleLogin} disabled={isOAuthLoading}>
                      {isOAuthLoading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <GoogleIcon className="mr-2 h-5 w-5" />}
                      Continue with Google
                    </Button>
                    <Button type="button" variant="outline" className="w-full bg-secondary/50 border-border hover:bg-secondary" onClick={handleAppleLogin} disabled={isOAuthLoading}>
                      {isOAuthLoading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <AppleIcon className="mr-2 h-5 w-5" />}
                      Continue with Apple
                    </Button>
                    {!showEmailForm && (
                      <Button type="button" variant="outline" className="w-full bg-secondary/50 border-border hover:bg-secondary" onClick={() => setShowEmailForm(true)} disabled={isOAuthLoading}>
                        <Mail className="mr-2 h-5 w-5" />
                        Continue with Email
                      </Button>
                    )}
                  </div>

                  {showEmailForm && (
                    <form onSubmit={mode === "signup" ? handleSignUp : handleLogin} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="auth-email">Email</Label>
                        <Input id="auth-email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-secondary border-border" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="auth-password">Password</Label>
                        <Input id="auth-password" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="bg-secondary border-border" />
                      </div>
                      {mode === "signup" && (
                        <div className="space-y-2">
                          <Label htmlFor="auth-confirm">Confirm Password</Label>
                          <Input id="auth-confirm" type="password" placeholder="••••••••" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} className="bg-secondary border-border" />
                        </div>
                      )}
                      {mode === "signin" && (
                        <div className="flex justify-end">
                          <button type="button" className="text-xs text-muted-foreground hover:text-primary transition-colors" onClick={() => setMode("forgot")}>
                            Forgot password?
                          </button>
                        </div>
                      )}
                      <Button type="submit" className="w-full" variant="glow" disabled={isLoading || isOAuthLoading}>
                        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {mode === "signup" ? "Create Account" : "Sign In"}
                      </Button>
                    </form>
                  )}

                  <p className="text-center text-sm text-muted-foreground">
                    {mode === "signin" ? (
                      <>Don't have an account?{" "}<button type="button" className="text-primary hover:underline font-medium" onClick={() => setMode("signup")}>Sign up</button></>
                    ) : (
                      <>Already have an account?{" "}<button type="button" className="text-primary hover:underline font-medium" onClick={() => setMode("signin")}>Sign in</button></>
                    )}
                  </p>
                </>
              )}

              {showNetworkError && <NetworkErrorBanner onRetry={handleRetry} isRetrying={isLoading} />}
            </div>
          </div>
        </div>
      </Card>
      </div>
    </div>
  );
};

export default Auth;
